import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  BASE_FONTS, customKey, fontCss, fontLabel, getCustomFonts, onFontsChange,
} from "../lib/fonts";

export function FontSelect({ value, onChange, testId, className }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [customs, setCustoms] = useState(getCustomFonts());
  useEffect(() => onFontsChange(setCustoms), []);

  const query = q.trim().toLowerCase();
  const base = BASE_FONTS.filter((f) => !query || f.label.toLowerCase().includes(query));
  const custom = customs.filter((f) => !query || f.name.toLowerCase().includes(query));
  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ("");
  };

  const renderItem = (v, label, css, sub) => (
    <button
      key={v}
      type="button"
      data-testid={`${testId}-opt-${v}`}
      onClick={() => pick(v)}
      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-blue-50 text-left transition-soft"
    >
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-slate-800 truncate" style={{ fontFamily: css }}>
          {label}
        </span>
        {sub && <span className="block text-[10px] text-slate-400 truncate">{sub}</span>}
      </span>
      {value === v && <Check size={13} className="text-blue-800 shrink-0" />}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`flex items-center justify-between gap-2 h-8 w-full rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700 hover:border-blue-300 transition-soft ${className || ""}`}
        >
          <span className="truncate" style={{ fontFamily: fontCss(value) }}>
            {fontLabel(value || "serif")}
          </span>
          <ChevronsUpDown size={12} className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1.5" align="start" data-testid={`${testId}-panel`}>
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-slate-100 mb-1">
          <Search size={12} className="text-slate-400" />
          <input
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search fonts…"
            className="flex-1 bg-transparent text-xs outline-none"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {custom.length > 0 && (
            <>
              <div className="px-2.5 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Custom fonts
              </div>
              {custom.map((f) =>
                renderItem(customKey(f.id), f.name, fontCss(customKey(f.id)), f.style)
              )}
            </>
          )}
          {base.length > 0 && (
            <>
              <div className="px-2.5 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Built-in fonts
              </div>
              {base.map((f) => renderItem(f.value, f.label, f.css))}
            </>
          )}
          {custom.length === 0 && base.length === 0 && (
            <div className="px-2.5 py-3 text-xs text-slate-400 text-center">
              No fonts match "{q}"
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
